import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PieChart, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';
import { ReportHeader, DateRange, StatCard, monthFirst, today } from './_shared';
import EmailReportButton from './EmailReportButton';

const COLOR_RAMP = [
  'bg-rose-500', 'bg-amber-500', 'bg-emerald-500', 'bg-sky-500',
  'bg-violet-500', 'bg-pink-500', 'bg-teal-500', 'bg-orange-500',
  'bg-cyan-500', 'bg-lime-500', 'bg-slate-500', 'bg-indigo-500',
];

const ExpensesReport = () => {
  const { authHeader } = useAuth();
  const [from, setFrom] = useState(monthFirst());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const { data: d } = await axios.get(`${API}/reports/expenses?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, from, to]);

  useEffect(() => { load(); }, [load]);

  const max = (data?.by_category || []).reduce((m, b) => Math.max(m, b.amount), 0) || 1;
  const total = data?.total || 0;

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="report-expenses">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ReportHeader icon={PieChart} title="Expenses Report" />
        <EmailReportButton
          reportType="expenses"
          getFilters={() => ({ date_from: from, date_to: to })}
        />
      </div>
      <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={load} />

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Entries" value={data?.count ?? 0} mono={false} testid="exprep-count" />
        <StatCard label="Total spent" value={fmtINR(data?.total)} color="text-rose-700" testid="exprep-total" />
        <StatCard label="Auto-COGS portion" value={fmtINR(data?.auto_cogs_total)} color="text-amber-700" hint="Stock consumption auto-posted" />
        <StatCard label="Partial payments" value={fmtINR(data?.partial_payment_total)} color="text-violet-700" hint="From Record Payment modal" />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {/* By category */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700">By Category</h2>
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
          ) : (data?.by_category || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">No expenses in this period.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.by_category.map((b, idx) => (
                <li key={b.category} className="px-4 py-2.5">
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-sm font-semibold text-slate-700">{b.category}</div>
                    <div className="text-sm font-mono">{fmtINR(b.amount)} <span className="text-[10px] text-slate-400">· {((b.amount / total) * 100).toFixed(1)}%</span></div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${COLOR_RAMP[idx % COLOR_RAMP.length]} rounded-full`} style={{ width: `${Math.max(2, (b.amount / max) * 100).toFixed(1)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* By method */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700">By Payment Method</h2>
          </div>
          {(data?.by_method || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">—</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.by_method.map((b) => (
                <li key={b.method} className="px-4 py-2.5 flex items-baseline justify-between">
                  <div className="text-sm font-semibold text-slate-700">{b.method}</div>
                  <div className="text-sm font-mono">{fmtINR(b.amount)} <span className="text-[10px] text-slate-400">· {((b.amount / total) * 100).toFixed(1)}%</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Top vendors */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">Top Vendors</h2>
        </div>
        {(data?.top_vendors || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No vendor activity in this period.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
              <tr>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Vendor / Paid To</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Entries</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.top_vendors.map((v) => (
                <tr key={v.vendor} className="hover:bg-slate-50">
                  <td className="px-3 py-1.5 text-slate-800">{v.vendor}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{v.entries}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold text-rose-700">{fmtINR(v.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExpensesReport;
